import * as Dialog from '@radix-ui/react-dialog';
import { type DialogContentProps } from '@radix-ui/react-dialog';
import { type HTMLAttributes, forwardRef, type ReactNode } from 'react';

import { Icon } from '~/components/ui/icon.tsx';
import { cn } from '~/styles/extended-theme';

type Width = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type ModalProps = HTMLAttributes<HTMLDivElement> & {
  open?: boolean;
  onClose?: () => void;
  onOpenAutoFocus?: DialogContentProps['onOpenAutoFocus'];
  disableClose?: boolean;
  title?: ReactNode;
  maxWidth?: Width;
  className?: string;
  closeClassName?: string;
  dialogContentProps?: Pick<DialogContentProps, 'className'>;
};

const getMaxWidthClass = (maxWidth: Width) => {
  switch (maxWidth) {
    case 'xl':
      return 'max-w-xl';
    case 'lg':
      return 'max-w-lg';
    case 'md':
      return 'max-w-md';
    case 'sm':
      return 'max-w-sm';
    case 'xs':
      return 'max-w-xs';
    default:
      return '';
  }
};

const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      children,
      open,
      onClose,
      onOpenAutoFocus,
      disableClose,
      title,
      maxWidth = 'lg',
      className,
      closeClassName,
      dialogContentProps,
    },
    ref,
  ): ReactNode => {
    const handleClose = (e: Event) => {
      if (disableClose) e?.preventDefault();
    };
    return (
      <Dialog.Root open={open} onOpenChange={(open) => !open && onClose && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay
            forceMount
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <Dialog.Content
            className={cn(
              'fixed left-[50%] top-[50%] z-50 grid max-h-[96dvh] w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-lg border bg-background px-6 py-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] tablet:px-10 tablet:pb-[55px] tablet:pt-12 md:w-full',
              getMaxWidthClass(maxWidth),
              dialogContentProps?.className,
            )}
            tabIndex={undefined}
            ref={ref}
            aria-describedby={undefined}
            onOpenAutoFocus={onOpenAutoFocus}
            onEscapeKeyDown={handleClose}
            onInteractOutside={handleClose}
            onPointerDownOutside={handleClose}
          >
            <Dialog.Title asChild>
              {typeof title === 'string' ? (
                <h4 className="text-3xl font-medium leading-tight text-gray-900">{title}</h4>
              ) : (
                title
              )}
            </Dialog.Title>
            <div className={cn('pt-4', className)}>{children}</div>
            <Dialog.Close
              disabled={disableClose}
              className={cn(
                'absolute right-4 top-7 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground tablet:right-8 tablet:top-14',
                closeClassName,
              )}
            >
              <Icon name="x" className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  },
);

Modal.displayName = 'Modal';

export { Modal };
